import {ApplicationRef, Injectable} from '@angular/core';
import {SwUpdate} from '@angular/service-worker';
import {concat, interval} from 'rxjs';
import {first} from 'rxjs/operators';
import {AlertService} from './alert.service';
import {LoggingService} from './logging.service';
import {SessionService} from './session.service';

export const applyUpdate = () => {
  const logger = SoftwareUpdateService.logger;
  const updates = SoftwareUpdateService.updates;

  if (updates.isEnabled) {
    logger.log('Applying update...');
    updates.activateUpdate().then(() => {
      logger.log('Update applied!');

      document.location.reload();
    }).catch(e => logger.log('Error applying update', e));
  } else {
    logger.log('Cannot check for updates in development');
  }
};

@Injectable()
export class SoftwareUpdateService {

  static updates: SwUpdate;
  static logger: LoggingService;

  private isVisible = true;
  private notifyOnUpdates = true;

  constructor(private appRef: ApplicationRef,
              private updates: SwUpdate,
              private session: SessionService,
              private logger: LoggingService,
              private alerts: AlertService) {

    logger.log('Software Update Service: Loaded');

    SoftwareUpdateService.logger = logger;
    SoftwareUpdateService.updates = updates;

    // Allow the app to stabilize first, before starting polling for updates with `interval()`.
    const appIsStable$ = appRef.isStable.pipe(first(isStable => isStable === true));
    const everyThreeHours$ = interval(3 * 60 * 60 * 1000);
    const everyThreeHoursOnceAppIsStable$ = concat(appIsStable$, everyThreeHours$);

    everyThreeHoursOnceAppIsStable$.subscribe(() => this.checkForUpdate());

    this.setupVisibilityListener();

    updates.unrecoverable.subscribe(event => {
      this.alerts.setCurrentAlert({
        type: 'danger',
        message: `An error occurred that we cannot recover from: ${event.reason}`,
        action: {
          title: 'Reload Page',
          function: window.location.reload
        }
      });
    });

    updates.available.subscribe(event => {
      logger.log('Update available:', event);
      if (this.isVisible && this.notifyOnUpdates) {
        this.promptUserUpdate();
      } else {
        applyUpdate();
      }
    });

    session.notifyOnUpdates.subscribe(notifyOnUpdates => this.notifyOnUpdates = notifyOnUpdates);
  }

  promptUserUpdate() {
    this.alerts.setCurrentAlert({
      type: 'info',
      message: 'A new version of TORCH is available',
      action: {
        title: 'Apply Update',
        function: applyUpdate
      }
    }, 10 * 10 * 10 * 10 * 10);
  }

  checkForUpdate() {
    if (this.updates.isEnabled) {
      this.updates.checkForUpdate().then(() => this.logger.log('Checked for PWA updates successfully'));
    } else {
      this.logger.log('Cannot check for updates in development');
    }
  }

  private setupVisibilityListener() {
    document.addEventListener('visibilitychange', () => {
      if (!this.isVisible && !document.hidden) {
        this.checkForUpdate();
      }

      this.isVisible = !document.hidden;
    });
  }
}
